import type { Detail } from './figure';

/**
 * Presentation-only state shared by the character views, which (pedestrians, police) are plain
 * classes without the game context: the detail level for the current render quality, where the
 * player is and what just happened nearby (for heads turning), and recent hits (for flinches and
 * staggers). Writers: the render system (quality), the player view (position, own hits), the weapons
 * view (hits and shots). Readers: the ped, police, player and remote-player views. Nothing here feeds
 * back into the simulation.
 */

export type CharacterDetail = Detail | 'low';

let detail: CharacterDetail = 'high';

/** Render quality → character detail: HIGH full, MEDIUM (and phones, capped there) mid, LOW low. */
export function updateCharacterLook(quality: 'low' | 'medium' | 'high'): void {
  detail = quality === 'high' ? 'high' : quality === 'medium' ? 'mid' : 'low';
}

export function characterDetail(): CharacterDetail {
  return detail;
}

// --- view culling ----------------------------------------------------------------------------------

/**
 * The crowd meshes are single InstancedMeshes that cannot be frustum-culled as a whole, so the crowd
 * renderer skips characters outside the camera's view instead (crowd.ts). Writer: the render system,
 * once per frame after the camera has moved (the crowd views of the next frame read it). A character
 * is kept when its root lies within CULL_MARGIN of every frustum plane: wide enough for limbs, a long
 * evening shadow falling into view and one frame of ordinary camera motion.
 */
export const CULL_MARGIN = 9;
const planes = new Float32Array(24);
let cullOn = false;

/** Frustum planes (normal · p + constant ≥ 0 inside), or null to draw everyone. */
export function setCrowdView(frustum: readonly { normal: { x: number; y: number; z: number }; constant: number }[] | null): void {
  cullOn = !!frustum && frustum.length >= 6;
  if (!frustum || !cullOn) return;
  for (let i = 0; i < 6; i++) {
    const p = frustum[i]!;
    planes[i * 4] = p.normal.x;
    planes[i * 4 + 1] = p.normal.y;
    planes[i * 4 + 2] = p.normal.z;
    planes[i * 4 + 3] = p.constant;
  }
}

/** True when a character rooted at (x, y, z) may be on screen (always true until setCrowdView). */
export function crowdInView(x: number, y: number, z: number): boolean {
  if (!cullOn) return true;
  for (let i = 0; i < 24; i += 4) {
    if (planes[i]! * x + planes[i + 1]! * (y + 1) + planes[i + 2]! * z + planes[i + 3]! < -CULL_MARGIN) return false;
  }
  return true;
}

// --- attention -------------------------------------------------------------------------------------

/** The local player's position (heads turn to a villain walking past). */
export const playerSpot = { x: 0, z: 0, valid: false, armed: false };

interface Attention {
  x: number;
  z: number;
  until: number;
  strength: number;
}
const ATTN = 8;
const attention: Attention[] = Array.from({ length: ATTN }, () => ({ x: 0, z: 0, until: -1, strength: 0 }));
let attnNext = 0;

/** Something loud happened at (x, z) (a shot, a crash): heads within `radius` turn to it until `until`. */
export function noteAttention(x: number, z: number, strength: number, until: number): void {
  const a = attention[attnNext]!;
  attnNext = (attnNext + 1) % ATTN;
  a.x = x;
  a.z = z;
  a.until = until;
  a.strength = strength;
}

/** Strongest live attention point within `radius` of (x, z) at time `now` (null if none). */
export function attentionNear(x: number, z: number, radius: number, now: number): Attention | null {
  let best: Attention | null = null;
  let bestScore = 0;
  for (const a of attention) {
    if (a.until < now) continue;
    const d = Math.hypot(a.x - x, a.z - z);
    if (d > radius) continue;
    const score = a.strength * (1 - d / radius);
    if (score > bestScore) {
      bestScore = score;
      best = a;
    }
  }
  return best;
}

/** Any attention point live at `now` (cheap early-out for crowds). */
export function anyAttention(now: number): boolean {
  for (const a of attention) if (a.until >= now) return true;
  return false;
}

// --- hits ------------------------------------------------------------------------------------------

export type HitTarget = 'ped' | 'cop' | 'player' | 'remote';

export interface HitNote {
  target: HitTarget;
  id: number;
  x: number;
  z: number;
  /** Where the blow came from (world). */
  fromX: number;
  fromZ: number;
  strength: number;
  time: number;
}
const HITS = 16;
const hits: HitNote[] = Array.from({ length: HITS }, () => ({ target: 'ped' as HitTarget, id: -1, x: 0, z: 0, fromX: 0, fromZ: 0, strength: 0, time: -99 }));
let hitNext = 0;
let lastHitTime = -99;

export function noteHit(target: HitTarget, id: number, x: number, z: number, fromX: number, fromZ: number, strength: number, time: number): void {
  const h = hits[hitNext]!;
  hitNext = (hitNext + 1) % HITS;
  h.target = target;
  h.id = id;
  h.x = x;
  h.z = z;
  h.fromX = fromX;
  h.fromZ = fromZ;
  h.strength = strength;
  h.time = time;
  lastHitTime = Math.max(lastHitTime, time);
}

/** How long the struck figure flashes (s): a warm-white tint pulse, full on contact, gone in a twelfth of a second. */
export const HIT_FLASH_S = 0.08;

/**
 * Tint pulse 0..1 for a figure hit `age` seconds ago with `strength` (the hit note's): the views feed it to
 * the figure (detailedPlayer `setHit`) or the crowd row (crowdBodies `iHit`; crowd.ts's instance colours on LOW). 0 before and after the pulse.
 */
export function hitFlash(age: number, strength: number): number {
  if (age < 0 || age >= HIT_FLASH_S || strength <= 0) return 0;
  return Math.min(1, 0.55 + 0.45 * strength) * (1 - age / HIT_FLASH_S);
}

/** True if any hit landed after `since` (cheap early-out for crowds). */
export function hitsSince(since: number): boolean {
  return lastHitTime > since;
}

/** Latest hit on `target` with `id` (id < 0: the nearest within 1.6 m of (x, z)) after `since`. */
export function latestHit(target: HitTarget, id: number, since: number, x = 0, z = 0): HitNote | null {
  let best: HitNote | null = null;
  for (const h of hits) {
    if (h.target !== target || h.time <= since) continue;
    if (id >= 0 ? h.id !== id : Math.hypot(h.x - x, h.z - z) > 1.6) continue;
    if (!best || h.time > best.time) best = h;
  }
  return best;
}

/** Body-relative direction (rad, 0 = in front, + = to the left) of a world point seen from (x, z) facing `yaw`. */
export function relativeYaw(x: number, z: number, yaw: number, tx: number, tz: number): number {
  const a = Math.atan2(tx - x, tz - z) - yaw;
  const t = Math.PI * 2;
  let r = a % t;
  if (r > Math.PI) r -= t;
  else if (r <= -Math.PI) r += t;
  return r;
}

/** For tests: forget every note. */
export function resetCharacterLook(): void {
  detail = 'high';
  cullOn = false;
  playerSpot.valid = false;
  for (const a of attention) a.until = -1;
  for (const h of hits) h.time = -99;
  lastHitTime = -99;
}
